import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageLoader } from "@/components/common/loader";
import { Card } from "@/components/ui/card";
import { getProfile, type PortalProfile as Profile } from "@/features/portal/api";

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 px-5 py-3.5">
      <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
        {label}
      </span>
      <span className="text-foreground">{value || "—"}</span>
    </div>
  );
}

/** `/portal/profile` — the client's own identity, read-only (updates go through
 * the firm). Tax IDs show only the last 4 digits. */
export function PortalProfile() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    getProfile()
      .then((p) => active && setProfile(p))
      .catch(() => {
        if (!active) return;
        setFailed(true);
        toast.error("Couldn't load your profile. Please refresh.");
      });
    return () => {
      active = false;
    };
  }, []);

  if (failed) {
    return (
      <div className="mx-auto w-full max-w-2xl py-8">
        <p className="text-muted-foreground text-sm">Your profile couldn't be loaded.</p>
      </div>
    );
  }
  if (profile === null) return <PageLoader />;

  return (
    <div className="mx-auto w-full max-w-2xl space-y-6 py-8">
      <h1 className="text-2xl font-semibold text-foreground">Profile</h1>
      <Card className="gap-0 divide-y overflow-hidden p-0">
        <Row label="Name" value={profile.name} />
        <Row
          label="Type"
          value={profile.clientType === "INDIVIDUAL" ? "Individual" : "Business"}
        />
        <Row label="Email" value={profile.email} />
        <Row
          label={profile.taxIdKind}
          value={profile.taxIdLast4 ? `•••••${profile.taxIdLast4}` : "—"}
        />
        <Row label="Country" value={profile.country} />
      </Card>
      <p className="text-muted-foreground text-sm">
        Need to update any of these? Contact your tax team and they'll make the change.
      </p>
    </div>
  );
}
